import type { EmailTask, TaskPriority, TaskStatus } from '../types'

export type StatusFilter = TaskStatus | 'all'
export type SortKey = 'seen' | 'due' | 'priority' | 'created' | 'title'

/** Nested tree for future graph / Gantt views (not used by the flat list). */
export type TaskNode = {
  task: EmailTask
  children: TaskNode[]
}

const PRIORITY_RANK: Record<TaskPriority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
}

export function depthOf(task: EmailTask, byId: Map<string, EmailTask>): number {
  let depth = 0
  let cur: EmailTask | undefined = task
  const seen = new Set<string>()
  while (cur?.parent_id && byId.has(cur.parent_id) && !seen.has(cur.id)) {
    seen.add(cur.id)
    depth += 1
    cur = byId.get(cur.parent_id)
  }
  return depth
}

/** Depth-first walk: parents before children; children indented in list order. */
export function flattenTree(tasks: EmailTask[]): EmailTask[] {
  const byId = new Map(tasks.map((t) => [t.id, t]))
  const children = new Map<string, EmailTask[]>()
  const roots: EmailTask[] = []
  for (const t of tasks) {
    if (t.parent_id && byId.has(t.parent_id)) {
      const list = children.get(t.parent_id) ?? []
      list.push(t)
      children.set(t.parent_id, list)
    } else {
      roots.push(t)
    }
  }
  const out: EmailTask[] = []
  const walk = (node: EmailTask) => {
    out.push(node)
    for (const child of children.get(node.id) ?? []) walk(child)
  }
  for (const root of roots) walk(root)
  return out
}

function ts(iso: string | null): number {
  if (!iso) return Number.POSITIVE_INFINITY
  const n = Date.parse(iso)
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n
}

function compareBy(a: EmailTask, b: EmailTask, sort: SortKey): number {
  switch (sort) {
    case 'title':
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    case 'created':
      return ts(a.created_at) - ts(b.created_at)
    case 'due': {
      const ad = ts(a.due_at)
      const bd = ts(b.due_at)
      if (ad !== bd) return ad - bd
      return ts(b.last_seen_at) - ts(a.last_seen_at)
    }
    case 'priority': {
      const ap = a.priority ? PRIORITY_RANK[a.priority] : 9
      const bp = b.priority ? PRIORITY_RANK[b.priority] : 9
      if (ap !== bp) return ap - bp
      return ts(a.due_at) - ts(b.due_at)
    }
    case 'seen':
    default:
      return ts(b.last_seen_at) - ts(a.last_seen_at)
  }
}

/**
 * Filter, then sort siblings (and roots) by sort key while preserving
 * parent→child nesting in the flattened output.
 */
export function prepareList(
  tasks: EmailTask[],
  opts: {
    status: StatusFilter
    query: string
    priority: TaskPriority | 'all'
    sort: SortKey
  },
): { rows: EmailTask[]; byId: Map<string, EmailTask> } {
  const q = opts.query.trim().toLowerCase()
  let pool = tasks
  if (opts.status !== 'all') {
    pool = pool.filter((t) => t.status === opts.status)
  }
  if (opts.priority !== 'all') {
    pool = pool.filter((t) => t.priority === opts.priority)
  }
  if (q) {
    pool = pool.filter((t) => {
      const hay = [
        t.title,
        t.detail ?? '',
        t.source_quote,
        t.owner_email ?? '',
        t.assigned_by_email ?? '',
        t.gmail_thread_id,
      ]
        .join('\n')
        .toLowerCase()
      return hay.includes(q)
    })
  }

  const byId = new Map(pool.map((t) => [t.id, t]))
  const children = new Map<string, EmailTask[]>()
  const roots: EmailTask[] = []
  for (const t of pool) {
    if (t.parent_id && byId.has(t.parent_id)) {
      const list = children.get(t.parent_id) ?? []
      list.push(t)
      children.set(t.parent_id, list)
    } else {
      roots.push(t)
    }
  }

  const sortSiblings = (list: EmailTask[]) =>
    [...list].sort((a, b) => compareBy(a, b, opts.sort))

  const out: EmailTask[] = []
  const walk = (node: EmailTask) => {
    out.push(node)
    for (const child of sortSiblings(children.get(node.id) ?? [])) walk(child)
  }
  for (const root of sortSiblings(roots)) walk(root)

  return { rows: out, byId }
}

export function childCount(taskId: string, tasks: EmailTask[]): number {
  return tasks.filter((t) => t.parent_id === taskId).length
}

/** Build a forest from a flat list (ready for graph / Gantt later). */
export function toForest(tasks: EmailTask[]): TaskNode[] {
  const byId = new Map(tasks.map((t) => [t.id, t]))
  const children = new Map<string, EmailTask[]>()
  const roots: EmailTask[] = []
  for (const t of tasks) {
    if (t.parent_id && byId.has(t.parent_id)) {
      const list = children.get(t.parent_id) ?? []
      list.push(t)
      children.set(t.parent_id, list)
    } else {
      roots.push(t)
    }
  }
  const build = (node: EmailTask): TaskNode => ({
    task: node,
    children: (children.get(node.id) ?? []).map(build),
  })
  return roots.map(build)
}

/**
 * Hide descendants of collapsed parents. `expanded` is a set of parent ids
 * that are open; parents are collapsed by default.
 */
export function visibleWithCollapse(
  rows: EmailTask[],
  byId: Map<string, EmailTask>,
  expanded: ReadonlySet<string>,
): EmailTask[] {
  return rows.filter((t) => {
    let cur: EmailTask | undefined = t
    const seen = new Set<string>()
    while (cur?.parent_id && byId.has(cur.parent_id) && !seen.has(cur.id)) {
      seen.add(cur.id)
      if (!expanded.has(cur.parent_id)) return false
      cur = byId.get(cur.parent_id)
    }
    return true
  })
}
